<template>
  <div class="filter-set location">
    <button
      class="filter-name"
      @click.stop="emit('toggle')"
      :class="{ 'in-use': searchUtils.hasLocationFilters(locationFilters) }"
      :aria-expanded="expanderVisible ? 'true' : 'false'"
      :aria-controls="'filter-location-input-panel'"
      id="location-toggle"
    >
      <span class="filter-name__label">Sijainti</span>
      <i class="toggle-expander icon-chevron-down"></i>
    </button>
    <expander :expanded="expanderVisible">
      <div
        :id="'filter-location-input-panel'"
        class="location-expander-panel"
        role="region"
        aria-labelledby="location-toggle"
      >
        <autocomplete
          :id="'filter-location-input'"
          :resultsId="'filter-location-results'"
          :emptyInput="true"
          :class="'location-input'"
        ></autocomplete>
      </div>
    </expander>
    <active-filters v-if="isMobile" showOnly="location"></active-filters>
  </div>
</template>

<script setup lang="ts">
import Expander from '../expander/expander.vue';
import Autocomplete from '@/app/components/autocomplete/autocomplete.vue';
import ActiveFilters from '../active-filters/active-filters.vue';
import searchUtils from '@/app/utils/search-utils';
import useSafeRoute from '@/app/composables/useSafeRoute';
import { useRouter } from 'vue-router';
import useEventBus from '@/app/composables/useEventBus';
import { useStore } from 'vuex';
import { onMounted, onUnmounted, toRefs } from 'vue';
import type { LocationFilters } from '@/app/utils/search-utils';

const route = useSafeRoute();
const router = useRouter();
const bus = useEventBus();
const store = useStore();

const props = withDefaults(
  defineProps<{
    expanderVisible?: boolean;
    locationFilters?: LocationFilters;
    isMobile?: boolean;
  }>(),
  {
    expanderVisible: undefined, // undefined is required for mobile
    locationFilters: () => ({}),
    isMobile: false,
  }
);

const { expanderVisible, locationFilters } = toRefs(props);

const emit = defineEmits(['toggle']);

const closeExpanderIfNotInMobile = () => {
  if (!props.isMobile) {
    emit('toggle');
  }
};

const handleAutocompleteSelected = (key: keyof LocationFilters, value: string) => {
  const [firstValue] = store.getters.getFilterValue(key).concat(value);
  if (firstValue) {
    const allowMultiple = !!route.value.query?.[key];
    searchUtils.addFilterToUrl(router, route.value, { [key]: firstValue }, allowMultiple);
  }
  closeExpanderIfNotInMobile();
};

onMounted(() => {
  bus.$on('autocompleteDistrictSelected', (district: { districtId: string }) => {
    handleAutocompleteSelected('districtId', district.districtId);
  });
  bus.$on('autocompleteStreetSelected', (street: { streetId: string }) => {
    handleAutocompleteSelected('streetId', street.streetId);
  });
  bus.$on('autocompleteRegionSelected', (region: { regionId: string }) => {
    handleAutocompleteSelected('regionId', region.regionId);
  });
  bus.$on('autocompleteCitySelected', (city: { cityId: string }) => {
    handleAutocompleteSelected('cityId', city.cityId);
  });
  bus.$on('autocompleteCountySelected', (county: { countyId: string }) => {
    handleAutocompleteSelected('countyId', county.countyId);
  });
  bus.$on('autocompletePostalCodeSelected', (postalCode: string) => {
    handleAutocompleteSelected('postalCode', postalCode.toString());
  });
});

onUnmounted(() => {
  bus.$off('autocompleteDistrictSelected', null);
  bus.$off('autocompleteStreetSelected', null);
  bus.$off('autocompleteRegionSelected', null);
  bus.$off('autocompleteCitySelected', null);
  bus.$off('autocompleteCountySelected', null);
  bus.$off('autocompletePostalCodeSelected', null);
});
</script>
